import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CalendarRange, Filter, IndianRupee, User } from "lucide-react";

export default function PartnerReservations() {
  const [bookings, setBookings] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    const params = {};
    if (from) params.checkin_from = from;
    if (to) params.checkin_to = to;
    const { data } = await api.get("/bookings/mine", { params });
    setBookings(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return bookings;
    const lc = q.toLowerCase();
    return bookings.filter((b) => (b.customer_name || "").toLowerCase().includes(lc) || (b.upi_txn_id || "").toLowerCase().includes(lc));
  }, [bookings, q]);

  const totalRevenue = filtered.reduce((s, b) => s + (b.price || 0) * (b.rooms || 1), 0);

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="hw-label text-blue-700">Reservations</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">Bookings at your hotels</h1>

        <div className="hw-card p-5 mt-8 flex flex-wrap gap-3 items-end" data-testid="reservations-filter">
          <div>
            <Label className="hw-label">Check-in from</Label>
            <Input data-testid="res-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm mt-1" />
          </div>
          <div>
            <Label className="hw-label">Check-in to</Label>
            <Input data-testid="res-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm mt-1" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="hw-label">Search traveller / UPI</Label>
            <Input data-testid="res-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or transaction id" className="rounded-sm mt-1" />
          </div>
          <Button onClick={load} className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold" data-testid="res-apply"><Filter className="w-4 h-4 mr-1" /> Apply</Button>
          <Button onClick={() => { setFrom(""); setTo(""); setQ(""); setTimeout(load, 50); }} variant="outline" className="rounded-sm" data-testid="res-clear">Clear</Button>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-5">
          <Stat label="Reservations" value={filtered.length} icon={CalendarRange} />
          <Stat label="Travellers" value={new Set(filtered.map((b) => b.customer_id)).size} icon={User} />
          <Stat label="Revenue (visible)" value={`₹${totalRevenue.toLocaleString("en-IN")}`} icon={IndianRupee} />
        </div>

        <div className="hw-card mt-5 overflow-x-auto" data-testid="reservations-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 hw-label text-slate-500">
              <tr>
                <th className="text-left p-3">Traveller</th>
                <th className="text-left p-3">Hotel</th>
                <th className="text-left p-3">Dates</th>
                <th className="text-left p-3">Rooms</th>
                <th className="text-left p-3">Price</th>
                <th className="text-left p-3">UPI</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500">No reservations match.</td></tr>
              ) : filtered.map((b) => (
                <tr key={b.booking_id} className="border-t border-slate-100 even:bg-slate-50/50" data-testid={`res-row-${b.booking_id}`}>
                  <td className="p-3 font-semibold text-slate-900">{b.customer_name || "—"}</td>
                  <td className="p-3 text-slate-700">{b.hotel_name}</td>
                  <td className="p-3 text-slate-700">{b.checkin || "—"} → {b.checkout || "—"}</td>
                  <td className="p-3">{b.rooms}</td>
                  <td className="p-3 hw-head font-bold">₹{b.price?.toLocaleString("en-IN")}</td>
                  <td className="p-3 hw-mono text-xs text-slate-500">{b.upi_txn_id || "—"}</td>
                  <td className="p-3"><span className="hw-chip border-emerald-300 bg-emerald-50 text-emerald-700 hw-label">{b.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="hw-card p-5 flex items-center justify-between">
      <div>
        <div className="hw-label text-slate-500">{label}</div>
        <div className="hw-head text-2xl font-black text-slate-900 mt-1">{value}</div>
      </div>
      <Icon className="w-6 h-6 text-emerald-600" />
    </div>
  );
}
